// GoLiveCelebration — fires after a campaign successfully toggles AI ON.
// Confetti + fox in a green ring + a live-activity line. Recreated from
// design_handoff_get_live_companion/prototype-companion.jsx `Celebrate`.
import React from 'react';
import { createPortal } from 'react-dom';
import { Check, ArrowRight } from 'lucide-react';
import SurfRing, { SurfAvatar } from './SurfRing';

const CONFETTI = [
  ['#22d3ee', 14, 18], ['#6366f1', 80, 12], ['#0891b2', 150, 22], ['#818cf8', 300, 16], ['#22d3ee', 360, 12],
];

export default function GoLiveCelebration({ campaignName, activityName, onClose, onWatch }) {
  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-surfox-ink/50 motion-safe:animate-sfFade"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Your AI is live"
    >
      <div
        className="relative w-[460px] max-w-[calc(100vw-32px)] bg-white rounded-[24px] overflow-hidden motion-safe:animate-sfPanelIn shadow-[0_30px_70px_-16px_rgba(15,23,42,0.5)]"
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="absolute inset-0"
          style={{ background: 'radial-gradient(120% 80% at 50% -10%, rgba(34,211,238,0.16), transparent 60%)' }}
        />
        {CONFETTI.map(([color, left, s], i) => (
          <div
            key={i}
            className="absolute rounded-[4px] opacity-80"
            style={{ top: 18 + (i % 2) * 10, left, width: s, height: s, background: color, transform: `rotate(${(i - 2) * 12}deg)` }}
          />
        ))}

        <div className="relative px-9 pt-10 pb-7 text-center">
          <div className="inline-flex relative">
            <SurfRing size={92} stroke={5} pct={1} color="#16a34a"><SurfAvatar size={66} radius={18} ring /></SurfRing>
            <span className="absolute -bottom-0.5 -right-0.5 flex w-7 h-7 rounded-full bg-surfox-green border-[3px] border-white items-center justify-center">
              <Check size={15} className="text-white" strokeWidth={3} />
            </span>
          </div>

          <div className="text-[25px] font-extrabold text-surfox-ink tracking-[-0.6px] mt-5">Your AI is live.</div>
          <div className="text-[14px] text-surfox-muted mt-1.5 leading-[1.55] max-w-[330px] mx-auto">
            <b className="text-[#334155]">{campaignName}</b> is on. Your AI is texting your leads right now and will surface the hot ones in Pulse.
          </div>

          {activityName && (
            <div className="flex items-center gap-2.5 w-fit mx-auto mt-5 px-3.5 py-2.5 bg-surfox-rail rounded-xl border border-surfox-line">
              <span className="relative inline-flex w-[7px] h-[7px]">
                <span className="absolute inset-0 rounded-full bg-surfox-green" />
                <span className="absolute -inset-[3px] rounded-full border-2 border-surfox-green opacity-35" />
              </span>
              <span className="text-[12.5px] text-[#334155] font-medium">Your AI just messaged <b>{activityName}</b></span>
              <span className="text-[11.5px] text-surfox-faint">now</span>
            </div>
          )}

          <div className="flex gap-2.5 mt-6">
            <button
              onClick={onClose}
              className="flex-none h-12 px-[18px] rounded-[13px] text-[13.5px] font-bold text-surfox-muted border border-surfox-line flex items-center hover:bg-surfox-rail"
            >
              Keep setting up
            </button>
            <button
              onClick={onWatch}
              className="flex-1 h-12 rounded-[13px] text-white flex items-center justify-center gap-2 text-[14.5px] font-bold bg-gradient-to-br from-surfox-cyan400 to-surfox-cyan shadow-[0_8px_18px_rgba(8,145,178,0.3)] hover:brightness-105"
            >
              Watch it work in Pulse <ArrowRight size={16} />
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
